import { reaction } from "mobx";
import { observer } from "mobx-react";
import { Button, Tooltip, Typography, Row, Col } from "antd";

import * as NF from "../../../../../components/OFTable/filters/NumericalFilter";
import * as SF from "../../../../../components/OFTable/filters/StateFilter";
import * as DRF from "../../../../../components/OFTable/filters/DateRangeFilter";

import OFTable from "../../../../../components/OFTable/OFTable";
import { CrudTableButtons } from "../../../_common/CrudTableButtons";

import copy from "copy-to-clipboard";
import _ from "lodash";

import { translateKey as t } from "../../../../../locales/translation";

import { PartnersController } from "../../PartnersController";
import { AppStore } from "~/stores/AppStore";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCheckCircle, faCircleCheck, faCircleXmark, faCopy, faEdit, faEye, faGlobe, faHandshake, faIdCard, faTag, faTrash } from "@fortawesome/free-solid-svg-icons";
import { partnerEnvironmentHost } from "../../../../../utils/partnerEnvironment";

const PartnerTable = observer(({ store, controller }: { store: AppStore; controller: PartnersController }) => {
  const getRowIndex = (row: any) => {
    return controller.partners.findIndex((p) => p._id === row._id) + 1;
  };

  const structureWithAdditionalColumns = () => {
    const columns: Array<any> = [
      {
        Header: t("lblPartners", "Partners"),
        id: "card",
        disableFilters: false,
        accessor: (row) => row,
        Cell: (data) => renderPartnersInfo(data),
      },
      {
        Header: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
        id: "action",
        width: 40,
        disableSortBy: true,
        disableFilters: true,
        accessor: (row) => _.get(row, "_id"),
        Cell: (partner) => <Button onClick={() => controller.openEditForm(partner)} icon={controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />} size="small" />,
        responsiveRender: "lg",
      },
      {
        Header: t("lblIndex", "Index"),
        id: "index",
        align: "right",
        width: 40,
        accessor: (row) => getRowIndex(row),
        Filter: NF.NumericalFilter,
        filterMethod: NF.FilteringMethod,
        Cell: (row) => (
          <Typography.Text disabled style={{ cursor: "pointer" }}>
            {getRowIndex(row)}
          </Typography.Text>
        ),
        responsiveRender: "lg",
      },
      {
        Header: t("lblActive", "Active"),
        width: 50,
        id: "active",
        accessor: (row: any) => _.get(row, "active", false),
        Filter: (props) => (
          <SF.StateFilter
            {...props}
            structure={[
              { title: t("lblActive", "Active"), value: true },
              { title: t("lblInactive", "Inactive"), value: false },
            ]}
          />
        ),
        filterMethod: (value, query) => value === query,
        Cell: (data, value) => (value ? <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#52C41A" }} /> : <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />),
        responsiveRender: "lg",
      },
      {
        Header: t("lblClientRef", "ClientRef"),
        id: "clientRef",
        width: 150,
        accessor: (row: any) => _.get(row, "clientRef", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: t("lblName", "Name"),
        id: "name",
        width: 200,
        accessor: (row: any) => _.get(row, "name", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },

      {
        Header: t("lblDBName", "DB Name"),
        id: "dbName",
        width: 300,
        accessor: (row: any) => _.get(row, "dbName", ""),
        Cell: (data, value) => value,
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        // PRO-84: the OpenFleet stack (environment.providerUrl host) this partner is served by.
        Header: t("lblEnvironment", "Environment"),
        id: "environment",
        width: 220,
        accessor: (row: any) => partnerEnvironmentHost(row) || "",
        Cell: (data, value) => (value ? value : <Typography.Text type="secondary">{t("lblUnknownEnvironment", "Unknown environment")}</Typography.Text>),
        enableGlobalSearch: true,
        responsiveRender: "lg",
      },
      {
        Header: <Tooltip title={`${t("lblCreatedAt", "Created At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>{t("lblCreatedAt", "Created At")}</Tooltip>,
        id: "createdAt",
        width: 150,
        accessor: (row) => (_.get(row, "createdAt", "") ? dayjs(_.get(row, "createdAt")).format("DD/MM/YYYY HH:mm") : ""),
        rawValue: (row) => (_.get(row, "createdAt", 0) ? dayjs(_.get(row, "createdAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: <Tooltip title={`${t("lblUpdatedAt", "Updated At")} (${t("lblFormat", "Format")}: DD/MM/YYYY HH:mm)`}>{t("lblUpdatedAt", "Updated At")}</Tooltip>,
        id: "updatedAtTimestamp",
        width: 150,
        accessor: (row) => (_.get(row, "updatedAt", "") ? dayjs(_.get(row, "updatedAt")).format("DD/MM/YYYY HH:mm") : ""),
        rawValue: (row) => (_.get(row, "updatedAt", 0) ? dayjs(_.get(row, "updatedAt")).valueOf() : 0),
        excludeZero: true,
        Filter: (props) => <DRF.DateRangeFilter {...props} />,
        filterMethod: (value, query) => DRF.FilteringMethod(value, query, "DD/MM/YYYY HH:mm"),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
      {
        Header: t("lblUpdatedBy", "Updated By"),
        id: "updatedBy",
        width: 150,
        accessor: (row: any) => _.get(row, "updatedBy", ""),
        Cell: (data, value) => <Typography.Text style={{ cursor: "pointer" }}>{value}</Typography.Text>,
        responsiveRender: "lg",
      },
    ];

    return columns;
  };

  const structureWithoutAdditionalColumns = () => {
    return structureWithAdditionalColumns().filter((item) => item.id !== "index" && item.id !== "createdAt" && item.id !== "updatedAtTimestamp" && item.id !== "updatedBy");
  };

  const buildTableColumns = () => {
    reaction(
      () => controller.additionalColumnsAreVisible,
      (additionalColumnsAreVisible) => {
        if (additionalColumnsAreVisible) {
          if (controller?.tableInstance) controller.tableInstance.computeColumns(structureWithAdditionalColumns());
        } else {
          controller.tableInstance.computeColumns(structureWithoutAdditionalColumns());
        }
      }
    );

    return structureWithoutAdditionalColumns();
  };

  const getContextMenuObj = () => {
    return {
      title: null,
      tagObj: { title: "name" },
      singleMenus: (row) => [
        {
          name: controller.isReadOnly ? t("lblView", "View") : t("lblEdit", "Edit"),
          type: "item",
          icon: controller.isReadOnly ? <FontAwesomeIcon icon={faEye} /> : <FontAwesomeIcon icon={faEdit} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => controller.handleContextMenuEdit(row),
        },
        {
          name: t("lblDelete", "Delete"),
          type: "item",
          icon: <FontAwesomeIcon icon={faTrash} />,
          render: store.user?.roles.includes("ADMIN") && !controller.isReadOnly,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => controller.handleContextMenuDelete(row),
        },
        {
          name: t("lblCopyToClipboard", "Copy to Clipboard"),
          type: "item",
          icon: <FontAwesomeIcon icon={faCopy} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          func: () => copyRow(row),
        },
        {
          name: t("lblOther", "Other"),
          type: "subMenu",
          icon: <FontAwesomeIcon icon={faBars} />,
          render: true,
          disabled: controller.tableInstance.multiSelectedRows.totSelected > 1,
          subMenus: [
            {
              name: t("lblCopyClientRef", "Copy Client Ref"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: !row.clientRef,
              func: () => controller.copyText(row.clientRef),
            },
            {
              name: t("lblCopyName", "Copy Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: !row.name,
              func: () => controller.copyText(row.name),
            },
            {
              name: t("lblCopyDBName", "Copy DB Name"),
              type: "item",
              icon: <FontAwesomeIcon icon={faCopy} />,
              render: true,
              disabled: !row.dbName,
              func: () => controller.copyText(row.dbName),
            },
          ],
        },
      ],
    };
  };

  const copyRow = (row) => {
    let copyText: string = "";

    copyText += t("lblPartner", "Partner") + "\n";
    copyText += "● " + t("lblActive", "Active") + ": " + String(row.active) + "\n";
    copyText += "● " + t("lblClientRef", "Client Ref") + ": " + (row.clientRef || "") + "\n";
    copyText += "● " + t("lblName", "Name") + ": " + (row.name || "") + "\n";
    copyText += "● " + t("lblDBName", "DB Name") + ": " + (row.dbName || "") + "\n";
    copyText += "● " + t("lblEnvironment", "Environment") + ": " + (partnerEnvironmentHost(row) || "") + "\n";

    copy(copyText);
    store.displayNotification(t("lblRowCopiedToClipboard", "Row Copied to Clipboard"), "info", false);
  };

  const renderPartnersInfo = (partner) => {
    return (
      <div style={{ textAlign: "left" }}>
        <div style={{ fontWeight: "bold", textAlign: "left", borderBottom: "1px solid #000", marginBottom: 5 }}>
          <Row gutter={16} style={{ padding: 5 }}>
            <Col span={12}>
              <FontAwesomeIcon icon={faHandshake} style={{ marginRight: 10 }} />
              {_.get(partner, "name", "")}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              {partner?.active ? <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#52C41A" }} /> : <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#F5222D" }} />}
            </Col>
          </Row>
        </div>

        <Row gutter={16} style={{ padding: "0px 5px" }}>
          <Col span={8}>
            <div className="truncate">
              <FontAwesomeIcon icon={faTag} style={{ marginRight: 5 }} />
              {_.get(partner, "clientRef", "")}
            </div>
          </Col>
          <Col span={8}>
            <div className="truncate">
              <FontAwesomeIcon icon={faIdCard} style={{ marginRight: 5 }} />
              {_.get(partner, "dbName", "")}
            </div>
          </Col>
          <Col span={8}>
            <div className="truncate" title={_.get(partner, "environment.providerUrl", "")}>
              <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 5 }} />
              {partnerEnvironmentHost(partner) || t("lblUnknownEnvironment", "Unknown environment")}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderPartnersTable = () => {
    const { isReadOnly, crudCommon, openEditForm } = controller;

    const tableInfo: Array<any> = crudCommon.renderTableInfo().filter((info) => info.key !== 1);

    return (
      <OFTable
        ndnConfig={null}
        controller={controller}
        columns={buildTableColumns()}
        dataObj="partners"
        exportName={t("lblPartners", "Partners")}
        tableInstance={(instance) => controller.setTableInstance(instance)}
        showCheckbox={false}
        editableCells={false}
        globalSearch={true}
        tableInfo={tableInfo}
        fixedColumns={4}
        tableCardHeight={75}
        onCardClick={(row) => openEditForm(row)}
        onRowClick={(row) => controller.handleRowClick(row)}
        onMultiSelection={() => crudCommon.handleMultiselection()}
        multiSelectRowObj="multiSelectKeyPressed"
        shiftKeyFlag="shiftKeyPressed"
        menuButtons={[
          <CrudTableButtons
            controller={controller}
            key={"CrudTableButtons"}
            onCreate={() => controller.setNewPartner()}
            createButtonTitle={t("lblPartner", "Partner")}
            hideDeleteButton={!store.user?.roles.includes("ADMIN")}
            singleDeleteOnly={true}
            onDelete={() => controller.deletePartner()}
          />,
        ]}
        contextMenuObj={getContextMenuObj()}
        contextMenuWidth={200}
      />
    );
  };

  return renderPartnersTable();
});

export { PartnerTable };
